<script setup lang="ts">
import { computed, ref } from "vue";
import { UiDialog, UiText, UiChip, UiInfoBox, UiAccordion, UiIconButton, UiStandardButton, UiTooltip } from "@shopexperts/shared";
import { useToaster } from "@shopexperts/shared";
import type { AiSummaryKind } from "@shopexperts/legacy-api";
import {
  AI_RUN_TRIGGER_LABELS,
  AI_SUMMARY_STEP_LABELS,
  formatAiCost,
  formatAiDateTime,
  formatAiDuration,
  formatAiModel,
  formatAiTokens,
  shortPromptVersion,
  stringifyAiJson,
} from "@/app/composables/expert-manager/aiSummaries";
import { useExpertAiStepRun } from "@/app/composables/expert-manager/useExpertAiSummaries";
import AiRunStatusChip from "./AiRunStatusChip.vue";

const props = defineProps<{
  expertId: number;
  runId: number | null;
  /** Run id currently live for the summary kind this run belongs to, when applicable. */
  currentRunId?: number | null;
  isPublishing?: boolean;
}>();

const emit = defineEmits<{
  (e: "publish", payload: { kind: AiSummaryKind; runId: number }): void;
}>();

const open = defineModel<boolean>({ default: false });
const toaster = useToaster();

const runId = computed(() => props.runId);
const { run, isLoading, error, refetch } = useExpertAiStepRun(() => props.expertId, runId);

const summaryKind = computed<AiSummaryKind | null>(() => {
  if (run.value?.step === "profile_summary") return "profile";
  if (run.value?.step === "reviews_summary") return "reviews";
  return null;
});
const isCurrent = computed(() => run.value !== null && props.currentRunId === run.value.id);
const canPublish = computed(() => summaryKind.value !== null && run.value?.status === "succeeded" && !isCurrent.value);

const title = computed(() =>
  run.value ? `${AI_SUMMARY_STEP_LABELS[run.value.step]} · revision ${run.value.revision}` : "Run details",
);

const facts = computed(() => {
  if (!run.value) return [];
  const entries: Array<[string, string]> = [
    ["Run", `#${run.value.id}`],
    ["Trigger", AI_RUN_TRIGGER_LABELS[run.value.trigger] + (run.value.triggered_by ? ` by ${run.value.triggered_by.name ?? run.value.triggered_by.email ?? "admin"}` : "")],
    ["Model", formatAiModel(run.value.model)],
    ["Prompt version", shortPromptVersion(run.value.prompt_version) ?? "—"],
    ["Tokens", formatAiTokens(run.value) ?? "—"],
    ["Cost", formatAiCost(run.value.usage.cost) ?? "—"],
    ["Duration", formatAiDuration(run.value) ?? "—"],
    ["Started", formatAiDateTime(run.value.started_at)],
    ["Finished", formatAiDateTime(run.value.finished_at)],
  ];
  if (run.value.parent_run_id) entries.push(["Unblocked by", `run #${run.value.parent_run_id}`]);
  return entries;
});

const summaryOutput = computed(() => {
  const output = run.value?.output;
  if (!summaryKind.value || !output || typeof output !== "object") return null;
  const record = output as Record<string, unknown>;
  const list = (key: string) => (Array.isArray(record[key]) ? (record[key] as string[]) : []);
  return {
    summary: typeof record.summary === "string" ? record.summary : "",
    positives: summaryKind.value === "profile" ? list("recommended_for") : list("most_common_praise"),
    negatives: summaryKind.value === "profile" ? list("not_ideal_for") : list("most_common_concerns"),
  };
});

const outputJson = computed(() => stringifyAiJson(run.value?.output));
const inputJson = computed(() => stringifyAiJson(run.value?.input_snapshot));
const rawJson = computed(() => stringifyAiJson(run.value?.raw_response));

const copied = ref<string | null>(null);
async function copy(label: string, text: string) {
  try {
    await navigator.clipboard.writeText(text);
    copied.value = label;
    toaster.create({ title: `${label} copied`, type: "success" });
    setTimeout(() => (copied.value = null), 1500);
  } catch {
    toaster.create({ title: "Clipboard unavailable", type: "error" });
  }
}

function publish() {
  if (!summaryKind.value || !run.value) return;
  emit("publish", { kind: summaryKind.value, runId: run.value.id });
}
</script>

<template>
  <UiDialog v-if="open" v-model="open" size="2xl" :title="title" secondary-action-label="Close" @secondary-action="open = false">
    <div class="flex flex-col gap-5">
      <div v-if="isLoading" class="flex flex-col gap-3">
        <div v-for="n in 4" :key="n" class="h-4 animate-pulse rounded bg-se-gray-200" :class="n === 1 ? 'w-1/3' : 'w-full'" />
      </div>

      <UiInfoBox v-else-if="error" variant="error" title="Couldn't load this run">
        <div class="flex flex-col items-start gap-3">
          <UiText as="p" variant="p-small" class="text-se-gray-700" text="The run details could not be fetched." />
          <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetch()" />
        </div>
      </UiInfoBox>

      <template v-else-if="run">
        <div class="flex flex-wrap items-center gap-2">
          <AiRunStatusChip :status="run.status" size="sm" />
          <UiChip v-if="isCurrent" text="Live on profile" variant="success" size="xs" rounded="full" />
          <div class="ml-auto flex items-center gap-2">
            <UiTooltip v-if="summaryKind && !isCurrent" content="Make this revision the one shown on the public profile">
              <UiStandardButton
                variant="primary"
                size="sm"
                icon="check"
                label="Make live"
                :disabled="!canPublish || isPublishing"
                @click="publish"
              />
            </UiTooltip>
          </div>
        </div>

        <UiInfoBox v-if="run.error" variant="error" title="This run failed">
          <UiText as="p" variant="p-small" class="text-se-gray-700" :text="run.error" />
        </UiInfoBox>

        <dl class="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-se-gray-100 p-4 sm:grid-cols-3">
          <div v-for="[label, value] in facts" :key="label" class="flex min-w-0 flex-col">
            <UiText as="span" variant="caption" class="text-se-gray-500" :text="label" />
            <UiText as="span" variant="p-small" class="truncate text-se-dark" :text="value" />
          </div>
        </dl>

        <section v-if="summaryOutput" class="flex flex-col gap-3 rounded-lg border border-se-gray-200 p-4">
          <UiText as="h4" variant="p-small" class="font-semibold text-se-dark" text="Summary as written" />
          <UiText v-if="summaryOutput.summary" as="p" variant="p-small" :text="summaryOutput.summary" />
          <div v-if="summaryOutput.positives.length || summaryOutput.negatives.length" class="grid gap-3 sm:grid-cols-2">
            <ul v-if="summaryOutput.positives.length" class="flex flex-col gap-1">
              <li v-for="(item, index) in summaryOutput.positives" :key="`p-${index}`">
                <UiText as="span" variant="p-small" class="text-se-green" :text="`+ ${item}`" />
              </li>
            </ul>
            <ul v-if="summaryOutput.negatives.length" class="flex flex-col gap-1">
              <li v-for="(item, index) in summaryOutput.negatives" :key="`n-${index}`">
                <UiText as="span" variant="p-small" class="text-se-danger" :text="`− ${item}`" />
              </li>
            </ul>
          </div>
        </section>

        <section v-if="outputJson" class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <UiText as="h4" variant="p-small" class="font-semibold text-se-dark" text="Parsed output" />
            <UiIconButton icon="copy" aria-label="Copy output JSON" variant="ghost" @click="copy('Output', outputJson)" />
          </div>
          <pre class="max-h-80 overflow-auto rounded-lg bg-se-dark p-4 font-mono text-xs leading-relaxed text-se-white">{{ outputJson }}</pre>
        </section>

        <UiAccordion v-if="inputJson">
          <template #trigger>
            <UiText as="span" variant="p-small" class="font-semibold text-se-dark" text="Input snapshot (exactly what the model received)" />
          </template>
          <div class="flex flex-col gap-2 pt-3">
            <div class="flex justify-end">
              <UiIconButton icon="copy" aria-label="Copy input snapshot" variant="ghost" @click="copy('Input snapshot', inputJson)" />
            </div>
            <pre class="max-h-96 overflow-auto rounded-lg bg-se-gray-100 p-4 font-mono text-xs leading-relaxed text-se-dark">{{ inputJson }}</pre>
          </div>
        </UiAccordion>

        <UiAccordion v-if="rawJson">
          <template #trigger>
            <UiText as="span" variant="p-small" class="font-semibold text-se-dark" text="Raw provider response" />
          </template>
          <div class="flex flex-col gap-2 pt-3">
            <div class="flex justify-end">
              <UiIconButton icon="copy" aria-label="Copy raw response" variant="ghost" @click="copy('Raw response', rawJson)" />
            </div>
            <pre class="max-h-96 overflow-auto rounded-lg bg-se-gray-100 p-4 font-mono text-xs leading-relaxed text-se-dark">{{ rawJson }}</pre>
          </div>
        </UiAccordion>
      </template>
    </div>
  </UiDialog>
</template>
